import React, { useState } from 'react'
import Snackbar from '@mui/material/Snackbar'
import Alert, { AlertColor } from '@mui/material/Alert'

interface AlertData {
  id: number
  severity: AlertColor
  message: string
}

interface CustomAlertProps {
  alerts: AlertData[]
  onRemove: (id: number) => void
}

/**
 * CustomAlert component displays multiple alert messages using Material-UI Snackbar and Alert components.
 *
 * @component
 * @example
 * ```tsx
 * const [alerts, setAlerts] = useState<AlertData[]>([
 *   { id: 1, severity: 'error', message: 'An error occurred' },
 *   { id: 2, severity: 'success', message: 'Operation successful' }
 * ])
 *
 * const handleRemoveAlert = (id: number) => {
 *   setAlerts((prev) => prev.filter((alert) => alert.id !== id))
 * }
 *
 * <CustomAlert alerts={alerts} onRemove={handleRemoveAlert} />
 * ```
 *
 * @param {object} props - The component props.
 * @param {AlertData[]} props.alerts - Array of alerts to be displayed.
 * @param {function} props.onRemove - Callback function to remove the alert by id.
 * @returns {JSX.Element} The rendered CustomAlert component with multiple alerts.
 */
const CustomAlert: React.FC<CustomAlertProps> = ({ alerts, onRemove }) => {
  return (
    <>
      {alerts.map((alert) => (
        <Snackbar
          key={alert.id}
          open={true}
          autoHideDuration={6000}
          onClose={() => onRemove(alert.id)}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          style={{ marginTop: 60 * alert.id }} // Ajusta la posición vertical de las alertas
        >
          <Alert
            onClose={() => onRemove(alert.id)}
            severity={alert.severity}
            sx={{ width: '100%' }}
          >
            {alert.message}
          </Alert>
        </Snackbar>
      ))}
    </>
  )
}

export default CustomAlert
